import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core';
import { useIntl } from 'react-intl';
import { SimpleAutocomplete } from '~/modules/common/components/SearchAutocomplete';
import { useMeContext } from '~/modules/me';
import { useProjectDropdownHandlers } from './hooks';

const useStyles = makeStyles(() => ({
  closedProject: {
    opacity: 0.6
  }
}));

export const AdvancedSearchableProjectDropdown = ({
  label,
  value,
  onChange,
  disabled = false,
  showInactiveProjects: showInactiveProjectsProp
}) => {
  const classes = useStyles();
  const intl = useIntl();
  const { uri: currentUserUri } = useMeContext();

  const [showInactiveProjects, setShowInactiveProjects] = useState(
    showInactiveProjectsProp ?? false
  );
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState(value?.displayText || '');

  const handleToggleInactiveProjects = useCallback(() => {
    setShowInactiveProjects(prev => !prev);
  }, []);

  const handleOpen = useCallback(() => {
    setOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  const handleInputChangeWrapper = useCallback(
    (event, newInputValue, reason) => {
      if (reason === 'reset') {
        setInputValue(value?.displayText || '');
      } else if (reason === 'input') {
        setInputValue(newInputValue);
      }
    },
    [value]
  );

  const handleInputKeyDown = useCallback(
    event => {
      if (event.key === ' ' && !open && !event.target.value) {
        event.preventDefault();
        setOpen(true);
      }
    },
    [open]
  );

  const {
    loading,
    hasMore,
    options,
    handleInputChange,
    getOptionLabel,
    getOptionSelected,
    getOptionDisabled,
    renderInput: renderInputFromHook,
    renderOption,
    filterOptions,
    handleChange,
    showInactiveProjectsOptionId
  } = useProjectDropdownHandlers({
    value,
    showInactiveProjects,
    onToggleInactiveProjects: handleToggleInactiveProjects,
    onClose: handleClose,
    projectManagerUri: currentUserUri,
    label,
    onChange,
    classes,
    onInputChange: handleInputChangeWrapper
  });

  const renderInput = useCallback(
    params => {
      const inputProps = {
        ...params.inputProps,
        onKeyDown: event => {
          handleInputKeyDown(event);

          if (params.inputProps.onKeyDown) {
            params.inputProps.onKeyDown(event);
          }
        }
      };

      return renderInputFromHook({ ...params, inputProps });
    },
    [renderInputFromHook, handleInputKeyDown]
  );

  const filteredValue =
    value && value.id === showInactiveProjectsOptionId ? null : value;

  return (
    <SimpleAutocomplete
      dataQeId="ProjectDropdown"
      hasMore={hasMore}
      loading={loading}
      forcePopupIcon
      fullWidth
      value={filteredValue}
      inputValue={inputValue}
      disabled={disabled}
      open={open}
      onOpen={handleOpen}
      onClose={handleClose}
      onChange={handleChange}
      onInputChange={handleInputChange}
      getOptionLabel={getOptionLabel}
      options={options}
      renderInput={renderInput}
      renderOption={renderOption}
      getOptionSelected={getOptionSelected}
      getOptionDisabled={getOptionDisabled}
      optionTypeText={intl.formatMessage({ id: 'moreOptions.projects' })}
      filterOptions={filterOptions}
      disableCloseOnSelect
    />
  );
};

AdvancedSearchableProjectDropdown.propTypes = {
  value: PropTypes.any,
  label: PropTypes.node,
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
  showInactiveProjects: PropTypes.bool
};

export default AdvancedSearchableProjectDropdown;
